import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FIND_ASSIGNABLE_GROUPS,
  Group,
  SortDirectionEnum,
  cacheKeys,
  useCustomMutation,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  InfiniteDataGrid,
  SaveFormTop,
  SearchInput,
  TopModalBar,
  tokens,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { ModalButton } from '../modalButton';

type GroupAssignmentColumnProps = {
  entityId: string;
  entityName: string;
  currentGroups: Group[];
  workspaceId: string;
  mutation: string;
  buildInput: (
    entityId: string,
    workspaceId: string,
    added: { id: string }[],
    removed: { id: string }[]
  ) => { input: Record<string, unknown> };
  cacheKey: string;
};

export const GroupAssignmentColumn = ({
  entityId,
  entityName,
  currentGroups,
  workspaceId,
  mutation,
  buildInput,
  cacheKey,
}: GroupAssignmentColumnProps) => {
  const groupCount = currentGroups?.length ?? 0;

  return (
    <ModalButton
      label=""
      customButton={
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons
            name="account-group-outline"
            color={Colors.blackText}
            size={16}
            style={styles.icon}
          />
          <Text style={styles.countText}>{groupCount}</Text>
        </View>
      }
      modalComponent={({ onComplete }) => (
        <GroupAssignmentForm
          entityId={entityId}
          entityName={entityName}
          currentGroups={currentGroups}
          workspaceId={workspaceId}
          mutation={mutation}
          buildInput={buildInput}
          cacheKey={cacheKey}
          onComplete={onComplete}
        />
      )}
      containerStyle={styles.modalContainer}
    />
  );
};

type GroupAssignmentFormProps = {
  entityId: string;
  entityName: string;
  currentGroups: Group[];
  workspaceId: string;
  mutation: string;
  buildInput: (
    entityId: string,
    workspaceId: string,
    added: { id: string }[],
    removed: { id: string }[]
  ) => { input: Record<string, unknown> };
  cacheKey: string;
  onComplete: () => void;
};

const GroupAssignmentForm = ({
  entityId,
  entityName,
  currentGroups,
  workspaceId,
  mutation,
  buildInput,
  cacheKey,
  onComplete,
}: GroupAssignmentFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const initialGroupIds = (currentGroups ?? [])
    .filter((g) => g?.id)
    .map((g) => g.id);

  const [originalAssignments] = useState<string[]>(initialGroupIds);
  const [assignments, setAssignments] = useState<string[]>(initialGroupIds);
  const [keyword, setKeyword] = useState('');

  const { mutateAsync: updateGroups } = useCustomMutation({ query: mutation });

  const isDirty = () =>
    originalAssignments.length !== assignments.length ||
    originalAssignments.some((id) => !assignments.includes(id)) ||
    assignments.some((id) => !originalAssignments.includes(id));

  const handleSubmit = async () => {
    const addedIds = assignments.filter(
      (id) => !originalAssignments.includes(id)
    );
    const removedIds = originalAssignments.filter(
      (id) => !assignments.includes(id)
    );

    await updateGroups(
      buildInput(
        entityId,
        workspaceId,
        addedIds.map((id) => ({ id })),
        removedIds.map((id) => ({ id }))
      ),
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys(cacheKey).all,
          });
        },
      }
    );

    onComplete();
  };

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={entityName}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      <View style={styles.container}>
        <SearchInput
          value={keyword}
          onChangeText={setKeyword}
          onDebounce={setKeyword}
          debounceLength={3}
          backgroundColor={Colors.white}
          placeholder={t('groups.assignments.groupSearchPlaceholder')}
          height={44}
        />
        <View style={styles.assignmentContainer} onLayout={onLayout}>
          <GroupAssignmentList
            workspaceId={workspaceId}
            maxHeight={listMaxHeight}
            keyword={keyword}
            assignments={assignments}
            updateAssignments={setAssignments}
          />
        </View>
      </View>
    </SaveFormTop>
  );
};

type GroupAssignmentListProps = {
  workspaceId: string;
  maxHeight?: number;
  keyword?: string;
  assignments: string[];
  updateAssignments: (assignments: string[]) => void;
};

const useGroupAssignmentColumns = () => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();

  const columns: ColumnDef<unknown, never>[] = [];

  columns.push(
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: listStyles.checkboxCell },
    })
  );

  columns.push(
    columnHelper.accessor('name', {
      cell: (info) => (
        <Text style={[listStyles.nameLabel, listStyles.cellText]}>
          {info.getValue()}
        </Text>
      ),
      header: () => t('groups.assignments.name'),
      meta: { style: listStyles.cell },
    })
  );

  return columns;
};

const GroupAssignmentList = ({
  workspaceId,
  maxHeight,
  keyword,
  assignments,
  updateAssignments,
}: GroupAssignmentListProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filter, setFilter] = useState<any>(() => ({
    workspaceId,
    assignments,
    keyword,
  }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useGroupAssignmentColumns() as ColumnDef<any, any>[];

  useEffect(() => {
    setFilter({
      workspaceId,
      assignments,
      keyword,
    });
  }, [workspaceId, keyword]);

  return (
    <InfiniteDataGrid
      modelName="group"
      hideResultsLabel={true}
      query={FIND_ASSIGNABLE_GROUPS}
      filter={filter}
      pageSize={100}
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={t('groups.common.textEmpty')}
      dataPrefix="group.findAssignableGroups"
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
      updateRowsSelected={(rows) => {
        updateAssignments(rows.map((row) => row.id));
      }}
      isRowSelected={(row) => {
        return assignments.includes(row.id);
      }}
      enableScrollRefresh={true}
      showScrollIndicator={true}
    />
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: tokens.spacing.xxs,
  },
  countText: {
    fontSize: 16,
    lineHeight: 17,
    fontWeight: '500',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: tokens.spacing.xl,
    paddingTop: tokens.spacing['2xl'],
    paddingBottom: tokens.spacing.xl,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: tokens.spacing.xl,
  },
});

const listStyles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: tokens.spacing.xs,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.black,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
});
